<template>
  <div class="pwa-finance">
    <!-- 헤더 -->
    <div class="finance-header">
      <div class="header-row">
        <span class="page-title">회계</span>
        <button class="year-btn" @click="showYearPicker = true">
          {{ selectedYear }}년 <v-icon size="16">mdi-chevron-down</v-icon>
        </button>
      </div>
    </div>

    <!-- 요약 (컴팩트) -->
    <div class="summary-section">
      <div class="summary-main">
        <span class="label">예산 잔액</span>
        <span class="value" :class="{ red: budgetBalance < 0 }">
          {{ budgetBalance >= 0 ? '' : '-' }}{{ formatCurrency(Math.abs(budgetBalance)) }}원
        </span>
      </div>
      <div class="summary-row">
        <div class="item">
          <span class="dot green"></span>
          <span class="label">세입</span>
          <span class="value">{{ formatCurrency(totalRevenue) }}</span>
        </div>
        <div class="item">
          <span class="dot red"></span>
          <span class="label">세출</span>
          <span class="value">{{ formatCurrency(totalExpense) }}</span>
        </div>
      </div>
    </div>

    <!-- 탭 -->
    <div class="tabs">
      <button :class="{ active: activeTab === 'revenue' }" @click="activeTab = 'revenue'">세입</button>
      <button :class="{ active: activeTab === 'expense' }" @click="activeTab = 'expense'">세출</button>
    </div>

    <!-- 리스트 -->
    <div class="finance-list">
      <!-- 로딩 -->
      <template v-if="isLoading">
        <div v-for="n in 5" :key="n" class="finance-item skeleton">
          <div class="skeleton-icon"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-amount"></div>
        </div>
      </template>

      <!-- 빈 상태 -->
      <div v-else-if="currentList.length === 0" class="empty-state">
        <v-icon size="40" color="#d1d5db">mdi-currency-krw</v-icon>
        <span>항목이 없습니다</span>
      </div>

      <!-- 항목 -->
      <template v-else>
        <div
          v-for="(item, index) in currentList"
          :key="index"
          class="finance-item"
          @click="openItemDetail(item)"
        >
          <div class="icon" :class="activeTab">
            <v-icon size="16" color="white">
              {{ activeTab === 'revenue' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
            </v-icon>
          </div>
          <div class="info">
            <span class="name">{{ item.name }}</span>
            <span class="category">{{ item.category }}</span>
          </div>
          <span class="amount" :class="activeTab">
            {{ activeTab === 'revenue' ? '+' : '-' }}{{ formatCurrency(item.amount) }}
          </span>
        </div>

        <!-- 합계 -->
        <div class="total-row" :class="activeTab">
          <span>{{ activeTab === 'revenue' ? '세입' : '세출' }} 합계</span>
          <span>{{ formatCurrency(activeTab === 'revenue' ? totalRevenue : totalExpense) }}원</span>
        </div>
      </template>
    </div>

    <!-- 상세 바텀시트 -->
    <div class="sheet-overlay" :class="{ open: showDetailSheet }" @click="showDetailSheet = false"></div>
    <div class="sheet" :class="{ open: showDetailSheet }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span>항목 상세</span>
        <button @click="showDetailSheet = false"><v-icon size="20">mdi-close</v-icon></button>
      </div>
      <div class="sheet-body" v-if="selectedItem">
        <div class="detail-row">
          <span class="label">항목명</span>
          <span class="value">{{ selectedItem.name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">분류</span>
          <span class="value">{{ selectedItem.category }}</span>
        </div>
        <div class="detail-row">
          <span class="label">금액</span>
          <span class="value" :class="selectedItem.type === 'revenue' ? 'green' : 'red'">
            {{ formatCurrency(selectedItem.amount) }}원
          </span>
        </div>
        <div class="detail-row" v-if="selectedItem.note">
          <span class="label">비고</span>
          <span class="value">{{ selectedItem.note }}</span>
        </div>
      </div>
    </div>

    <!-- 연도 선택 -->
    <v-dialog v-model="showYearPicker" max-width="280">
      <v-card rounded="lg">
        <div class="year-picker">
          <div class="picker-title">연도 선택</div>
          <div class="year-list">
            <button
              v-for="year in availableYears"
              :key="year"
              :class="{ active: selectedYear === year }"
              @click="selectYear(year)"
            >
              {{ year }}년
              <v-icon v-if="selectedYear === year" size="18">mdi-check</v-icon>
            </button>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <LodingComponent :is-visible="isLoading" text="회계 정보를 불러오는 중..." />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

interface FinanceItem {
  id: number;
  name: string;
  category: string;
  amount: number;
  note?: string;
  type?: 'revenue' | 'expense';
}

const store = useStore();
const user = computed(() => store.state.user);

const isLoading = ref(false);
const activeTab = ref<'revenue' | 'expense'>('revenue');
const showDetailSheet = ref(false);
const showYearPicker = ref(false);
const selectedItem = ref<FinanceItem | null>(null);

const currentYear = new Date().getFullYear();
const selectedYear = ref(currentYear);
const availableYears = computed(() => Array.from({ length: 5 }, (_, i) => currentYear - 2 + i));

const revenueItems = ref<FinanceItem[]>([]);
const expenseItems = ref<FinanceItem[]>([]);

const currentList = computed(() => 
  activeTab.value === 'revenue' ? revenueItems.value : expenseItems.value
);

const totalRevenue = computed(() => 
  revenueItems.value.reduce((sum, item) => sum + (item.amount || 0), 0)
);

const totalExpense = computed(() => 
  expenseItems.value.reduce((sum, item) => sum + (item.amount || 0), 0)
);

const budgetBalance = computed(() => totalRevenue.value - totalExpense.value);

const fetchFinanceData = async () => {
  if (!user.value?.m_sisul_code) return;
  try {
    isLoading.value = true;
    const response = await axios.get(`/api/finance/budget/${user.value.m_sisul_code}/${selectedYear.value}`, { withCredentials: true });

    if (response.data.success) {
      revenueItems.value = response.data.data?.revenueItems || [];
      expenseItems.value = response.data.data?.expenseItems || [];
    } else {
      revenueItems.value = [
        { id: 1, name: '급여청구수입', category: '진료수입', amount: 50000000 },
        { id: 2, name: '정부지원금', category: '보조금', amount: 20000000 }
      ];
      expenseItems.value = [
        { id: 1, name: '직원 급여', category: '인건비', amount: 30000000 },
        { id: 2, name: '임대료', category: '임대료', amount: 5000000 },
        { id: 3, name: '운영비', category: '기타비용', amount: 3000000 }
      ];
    }
  } catch (error) {
    console.error('회계 데이터 조회 오류:', error);
    revenueItems.value = [
      { id: 1, name: '급여청구수입', category: '진료수입', amount: 50000000 },
      { id: 2, name: '정부지원금', category: '보조금', amount: 20000000 }
    ];
    expenseItems.value = [
      { id: 1, name: '직원 급여', category: '인건비', amount: 30000000 },
      { id: 2, name: '임대료', category: '임대료', amount: 5000000 },
      { id: 3, name: '운영비', category: '기타비용', amount: 3000000 }
    ];
  } finally {
    isLoading.value = false;
  }
};

const formatCurrency = (value: number) => {
  if (!value) return '0';
  return new Intl.NumberFormat('ko-KR').format(value);
};

const selectYear = (year: number) => {
  selectedYear.value = year;
  showYearPicker.value = false;
  fetchFinanceData();
};

const openItemDetail = (item: FinanceItem) => {
  selectedItem.value = { ...item, type: activeTab.value };
  showDetailSheet.value = true;
};

watch(selectedYear, () => fetchFinanceData());
onMounted(() => fetchFinanceData());
</script>

<style scoped lang="scss">
@import '@/assets/styles/fonts.scss';

.pwa-finance {
  min-height: 100vh;
  background: #fff;
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  font-family: $Noto_Sans_KR !important;
  * { font-family: $Noto_Sans_KR !important; }

  .finance-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #fff;
    padding: 12px 16px;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    border-bottom: 1px solid #f0f0f0;

    .header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .page-title { font-size: 22px; font-weight: 700; }
      .year-btn {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 6px 10px;
        background: #f5f5f5;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }
    }
  }

  .summary-section {
    padding: 16px;
    background: #1e3a8a;
    color: #fff;

    .summary-main {
      display: flex;
      flex-direction: column;
      margin-bottom: 12px;

      .label { font-size: 13px; opacity: 0.8; }
      .value { 
        font-size: 26px; 
        font-weight: 700; 
        &.red { color: #fca5a5; }
      }
    }

    .summary-row {
      display: flex;
      gap: 20px;

      .item {
        display: flex;
        align-items: center;
        gap: 6px;

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          &.green { background: #10b981; }
          &.red { background: #ef4444; }
        }
        .label { font-size: 13px; opacity: 0.8; }
        .value { font-size: 14px; font-weight: 600; }
      }
    }
  }

  .tabs {
    display: flex;
    border-bottom: 1px solid #f0f0f0;

    button {
      flex: 1;
      padding: 12px 0;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      font-size: 14px;
      color: #999;

      &.active {
        color: #1e3a8a;
        border-bottom-color: #1e3a8a;
        font-weight: 600;
      }
    }
  }

  .finance-list {
    .finance-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;

      &:active { background: #fafafa; }

      &.skeleton {
        .skeleton-icon { width: 32px; height: 32px; border-radius: 8px; background: #f0f0f0; }
        .skeleton-text { height: 14px; width: 100px; background: #f0f0f0; border-radius: 4px; }
        .skeleton-amount { height: 14px; width: 60px; background: #f0f0f0; border-radius: 4px; margin-left: auto; }
      }

      .icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.revenue { background: #10b981; }
        &.expense { background: #ef4444; }
      }

      .info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;

        .name { font-size: 15px; font-weight: 600; color: #111; }
        .category { font-size: 12px; color: #999; }
      }

      .amount {
        font-size: 15px;
        font-weight: 600;

        &.revenue { color: #059669; }
        &.expense { color: #dc2626; }
      }
    }

    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 14px 16px;
      margin: 12px 16px;
      border-radius: 8px;
      font-weight: 600;

      &.revenue { background: #ecfdf5; color: #059669; }
      &.expense { background: #fef2f2; color: #dc2626; }
    }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    color: #999;
    font-size: 14px;
    gap: 8px;
  }

  // 바텀시트
  .sheet-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 200;
    opacity: 0;
    visibility: hidden;
    transition: all 0.25s;
    &.open { opacity: 1; visibility: visible; }
  }

  .sheet {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 16px 16px 0 0;
    z-index: 201;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: 60vh;
    overflow: hidden;

    &.open { transform: translateY(0); }

    .sheet-handle {
      width: 36px;
      height: 4px;
      background: #ddd;
      border-radius: 2px;
      margin: 8px auto;
    }

    .sheet-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 17px;
      font-weight: 700;

      button {
        padding: 4px;
        background: #f5f5f5;
        border-radius: 50%;
        border: none;
      }
    }

    .sheet-body {
      padding: 16px;
    }
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child { border-bottom: none; }

    .label { font-size: 14px; color: #666; }
    .value { 
      font-size: 14px; 
      font-weight: 500; 
      &.green { color: #059669; }
      &.red { color: #dc2626; }
    }
  }

  // 연도 선택
  .year-picker {
    padding: 20px;

    .picker-title {
      font-size: 17px;
      font-weight: 700;
      margin-bottom: 16px;
      text-align: center;
    }

    .year-list {
      display: flex;
      flex-direction: column;
      gap: 4px;

      button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
        background: #f5f5f5;
        border: none;
        border-radius: 8px;
        font-size: 15px;
        color: #333;

        &.active {
          background: #1e3a8a;
          color: #fff;
          font-weight: 600;
        }
      }
    }
  }
}
</style>
